import React from "react";
import { useMerchantAuthUser } from "../auth/MerchantAuthContext";
import MerchantModal from "../components/MerchantModal";
import MerchantStatusBadge from "../components/MerchantStatusBadge";
import { pickMerchantField, toMerchantString } from "../data/merchantLegacyUtils";

function getObjectValue(value) {
  return value && typeof value === "object" ? value : {};
}

function pickSettingsField(sources, keys) {
  for (let index = 0; index < sources.length; index += 1) {
    const value = pickMerchantField(sources[index], keys, "");

    if (value !== null && value !== undefined && toMerchantString(value).trim() !== "") {
      return value;
    }
  }

  return "";
}

function formatSettingsValue(value) {
  return toMerchantString(value).trim();
}

function buildSettingsRow(label, value, kind) {
  const displayValue = formatSettingsValue(value);

  if (!displayValue) {
    return null;
  }

  return kind ? [label, displayValue, kind] : [label, displayValue];
}

function compactSettingsRows(rows) {
  return rows.filter(Boolean);
}

function buildBusinessRecordFromSession(authUser) {
  const rawContext = getObjectValue(authUser && authUser.rawContext);
  const rawBusiness = getObjectValue(rawContext.business);
  const businessProfile = getObjectValue(authUser && authUser.businessProfile);
  const sources = [rawBusiness, businessProfile, getObjectValue(authUser)];

  return {
    id: pickSettingsField(sources, ["id", "businessId", "merchantBusinessId", "companyId"]),
    name: pickSettingsField(sources, ["name", "foreign_name", "foreignName", "businessName", "companyName"]),
    businessIdentificationNumber: pickSettingsField(sources, [
      "business_identification_number",
      "businessIdentificationNumber",
      "business_registration_number",
      "businessRegistrationNumber",
      "uen",
    ]),
    industry: pickSettingsField(sources, ["industry", "businessIndustry"]),
    creationTime: pickSettingsField(sources, [
      "creation_time",
      "creationTime",
      "created_at",
      "createdAt",
      "onboarded",
    ]),
    phoneNumber: pickSettingsField(sources, ["phone_number", "phoneNumber"]),
    contactName: pickSettingsField(sources, ["contact_name", "contactName"]),
    settlementEmails: pickSettingsField(sources, ["settlement_emails", "settlementEmails"]),
    businessDescription: pickSettingsField(sources, [
      "business_description",
      "businessDescription",
      "description",
    ]),
    address: pickSettingsField(sources, ["address", "registered_address", "registeredAddress"]),
    businessSettlementType: pickSettingsField(sources, [
      "business_settlement_type",
      "businessSettlementType",
      "settlement_type",
      "settlementType",
      "settlement_model",
      "settlementModel",
    ]),
    currency: pickSettingsField(sources, ["currency", "settlement_currency", "settlementCurrency"]),
    bankName: pickSettingsField(sources, ["bank_name", "bankName"]),
    bankAccount: pickSettingsField(sources, ["bank_account", "bankAccount", "bank_account_number", "bankAccountNumber"]),
  };
}

function getTwoFactorStatus(authUser) {
  if (typeof (authUser && authUser.twoFactorEnabled) === "boolean") {
    return authUser.twoFactorEnabled ? "enabled" : "disabled";
  }

  const enabledTwoFactor = toMerchantString(authUser && authUser.enabledTwoFactor).trim();

  return enabledTwoFactor || "";
}

function renderValue(value, kind) {
  if (kind === "status-enabled") {
    return <MerchantStatusBadge status="enabled" />;
  }

  if (kind === "status-disabled") {
    return <MerchantStatusBadge status="disabled" label="Disabled" />;
  }

  if (kind === "merchant-badge-green" || kind === "merchant-badge-amber") {
    return <span className={"merchant-badge " + kind}>{value}</span>;
  }

  if (kind === "merchant-mono") {
    return <span className="merchant-mono">{value}</span>;
  }

  return value;
}

function buildSettingsColumns(businessRecord, authUser) {
  const twoFactorStatus = getTwoFactorStatus(authUser);
  const twoFactorKind =
    twoFactorStatus.toLowerCase() === "enabled"
      ? "status-enabled"
      : twoFactorStatus.toLowerCase() === "disabled"
        ? "status-disabled"
        : "";

  return [
    [
      {
        title: "Merchant Information",
        rows: compactSettingsRows([
          buildSettingsRow("Legal Name", businessRecord.name),
          buildSettingsRow("Merchant ID", businessRecord.id, "merchant-mono"),
          buildSettingsRow(
            "UEN",
            businessRecord.businessIdentificationNumber || businessRecord.businessRegistrationNumber,
            "merchant-mono",
          ),
          buildSettingsRow("Industry", businessRecord.industry),
          buildSettingsRow("Onboarded", businessRecord.creationTime),
        ]),
        button: "Edit Details",
        actionKey: "profile",
      },
      {
        title: "Security",
        rows: compactSettingsRows([
          buildSettingsRow("Two-Factor Auth", twoFactorStatus, twoFactorKind),
        ]),
      },
    ],
    [
      {
        title: "Settlement Configuration",
        rows: compactSettingsRows([
          buildSettingsRow("Model", businessRecord.businessSettlementType),
          buildSettingsRow("Currency", businessRecord.currency),
          buildSettingsRow("Bank", businessRecord.bankName),
          buildSettingsRow("Account", businessRecord.bankAccount, "merchant-mono"),
          buildSettingsRow("Settlement Email", businessRecord.settlementEmails),
        ]),
        button: "Edit Settlement",
        actionKey: "settlement",
      },
      {
        title: "Notifications",
        rows: [],
      },
    ],
  ];
}

function buildProfileFormState(businessRecord) {
  return {
    name: toMerchantString(businessRecord.name),
    businessIdentificationNumber: toMerchantString(
      businessRecord.businessIdentificationNumber || businessRecord.businessRegistrationNumber,
    ),
    industry: toMerchantString(businessRecord.industry),
    phoneNumber: toMerchantString(businessRecord.phoneNumber),
    contactName: toMerchantString(businessRecord.contactName),
    settlementEmails: toMerchantString(businessRecord.settlementEmails),
    businessDescription: toMerchantString(businessRecord.businessDescription),
    address: toMerchantString(businessRecord.address),
  };
}

function buildSettlementFormState(businessRecord) {
  return {
    businessSettlementType: toMerchantString(businessRecord.businessSettlementType),
    currency: toMerchantString(businessRecord.currency),
    bankName: toMerchantString(businessRecord.bankName),
    bankAccount: toMerchantString(businessRecord.bankAccount),
  };
}

export default function MerchantSettingsPage() {
  const authUser = useMerchantAuthUser();
  const shouldShowEditActions = false;
  const [businessRecord, setBusinessRecord] = React.useState(function getInitialBusinessRecord() {
    return buildBusinessRecordFromSession(authUser);
  });
  const [modalMode, setModalMode] = React.useState("");
  const [profileForm, setProfileForm] = React.useState(function getInitialProfileForm() {
    return buildProfileFormState(buildBusinessRecordFromSession(authUser));
  });
  const [settlementForm, setSettlementForm] = React.useState(function getInitialSettlementForm() {
    return buildSettlementFormState(buildBusinessRecordFromSession(authUser));
  });
  const [formError, setFormError] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(
    function syncBusinessRecordFromSession() {
      const nextBusinessRecord = buildBusinessRecordFromSession(authUser);

      setBusinessRecord(nextBusinessRecord);
      setProfileForm(buildProfileFormState(nextBusinessRecord));
      setSettlementForm(buildSettlementFormState(nextBusinessRecord));
    },
    [authUser],
  );

  function openModal(mode) {
    setModalMode(mode);
    setProfileForm(buildProfileFormState(businessRecord));
    setSettlementForm(buildSettlementFormState(businessRecord));
    setFormError("");
  }

  function closeModal() {
    setModalMode("");
    setFormError("");
    setIsSaving(false);
  }

  function handleProfileChange(event) {
    const fieldName = event.target.name;
    const nextValue = event.target.value;

    setProfileForm(function updateProfileForm(previousState) {
      return {
        ...previousState,
        [fieldName]: nextValue,
      };
    });
  }

  function handleSettlementChange(event) {
    const fieldName = event.target.name;
    const nextValue = event.target.value;

    setSettlementForm(function updateSettlementForm(previousState) {
      return {
        ...previousState,
        [fieldName]: nextValue,
      };
    });
  }

  function handleSave(event) {
    event.preventDefault();
    setIsSaving(false);
    setFormError("Merchant settings are read-only until the API update endpoint is available.");
  }

  const columns = buildSettingsColumns(businessRecord, authUser);
  const visibleColumns = columns
    .map(function filterColumnCards(column) {
      return column.filter(function hasVisibleRows(card) {
        return Array.isArray(card.rows) && card.rows.length > 0;
      });
    })
    .filter(function hasVisibleCards(column) {
      return column.length > 0;
    });

  return (
    <div className="merchant-page">
      <div className="merchant-page-header">
        <div className="merchant-page-title">Settings</div>
        <div className="merchant-page-subtitle">Merchant account configuration</div>
      </div>

      <div className="merchant-settings-grid">
        {visibleColumns.map(function renderColumn(column, columnIndex) {
          return (
            <div className="merchant-settings-column" key={columnIndex}>
              {column.map(function renderCard(card) {
                return (
                  <div className="merchant-card" key={card.title}>
                    <div className="merchant-settings-title">{card.title}</div>
                    {card.rows.map(function renderRow(row) {
                      return (
                        <div className="merchant-settings-row" key={row[0]}>
                          <span className="merchant-settings-key">{row[0]}</span>
                          <span className="merchant-settings-value">
                            {renderValue(row[1], row[2])}
                          </span>
                        </div>
                      );
                    })}
                    {shouldShowEditActions ? (
                      <div className="merchant-settings-actions">
                        {card.button ? (
                          <button
                            className="merchant-button merchant-button-outline"
                            type="button"
                            onClick={
                              card.actionKey
                                ? function onClick() {
                                    openModal(card.actionKey);
                                  }
                                : undefined
                            }
                          >
                            {card.button}
                          </button>
                        ) : null}
                        {card.buttons
                          ? card.buttons.map(function renderButton(label) {
                              return (
                                <button
                                  className="merchant-button merchant-button-outline"
                                  type="button"
                                  key={label}
                                >
                                  {label}
                                </button>
                              );
                            })
                          : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <MerchantModal
        open={Boolean(modalMode)}
        title={modalMode === "settlement" ? "Edit Settlement Configuration" : "Edit Merchant Details"}
        subtitle="Merchant settings are read-only until the API update endpoint is available."
        onClose={closeModal}
      >
        <form className="merchant-modal-form" onSubmit={handleSave}>
          {modalMode === "settlement" ? (
            <div className="merchant-modal-grid">
              <label className="merchant-form-group">
                <span className="merchant-form-label">Settlement Model</span>
                <input
                  className="merchant-form-input"
                  name="businessSettlementType"
                  value={settlementForm.businessSettlementType}
                  onChange={handleSettlementChange}
                />
              </label>
              <label className="merchant-form-group">
                <span className="merchant-form-label">Currency</span>
                <input
                  className="merchant-form-input"
                  name="currency"
                  value={settlementForm.currency}
                  onChange={handleSettlementChange}
                />
              </label>
              <label className="merchant-form-group">
                <span className="merchant-form-label">Bank Name</span>
                <input
                  className="merchant-form-input"
                  name="bankName"
                  value={settlementForm.bankName}
                  onChange={handleSettlementChange}
                />
              </label>
              <label className="merchant-form-group">
                <span className="merchant-form-label">Bank Account</span>
                <input
                  className="merchant-form-input"
                  name="bankAccount"
                  value={settlementForm.bankAccount}
                  onChange={handleSettlementChange}
                />
              </label>
            </div>
          ) : (
            <div className="merchant-modal-grid">
              <label className="merchant-form-group">
                <span className="merchant-form-label">Legal Name</span>
                <input
                  className="merchant-form-input"
                  name="name"
                  value={profileForm.name}
                  onChange={handleProfileChange}
                />
              </label>
              <label className="merchant-form-group">
                <span className="merchant-form-label">UEN</span>
                <input
                  className="merchant-form-input"
                  name="businessIdentificationNumber"
                  value={profileForm.businessIdentificationNumber}
                  onChange={handleProfileChange}
                />
              </label>
              <label className="merchant-form-group">
                <span className="merchant-form-label">Industry</span>
                <input
                  className="merchant-form-input"
                  name="industry"
                  value={profileForm.industry}
                  onChange={handleProfileChange}
                />
              </label>
              <label className="merchant-form-group">
                <span className="merchant-form-label">Contact Name</span>
                <input
                  className="merchant-form-input"
                  name="contactName"
                  value={profileForm.contactName}
                  onChange={handleProfileChange}
                />
              </label>
              <label className="merchant-form-group">
                <span className="merchant-form-label">Phone Number</span>
                <input
                  className="merchant-form-input"
                  name="phoneNumber"
                  value={profileForm.phoneNumber}
                  onChange={handleProfileChange}
                />
              </label>
              <label className="merchant-form-group">
                <span className="merchant-form-label">Settlement Email</span>
                <input
                  className="merchant-form-input"
                  name="settlementEmails"
                  value={profileForm.settlementEmails}
                  onChange={handleProfileChange}
                />
              </label>
              <label className="merchant-form-group merchant-form-group-span-2">
                <span className="merchant-form-label">Address</span>
                <input
                  className="merchant-form-input"
                  name="address"
                  value={profileForm.address}
                  onChange={handleProfileChange}
                />
              </label>
              <label className="merchant-form-group merchant-form-group-span-2">
                <span className="merchant-form-label">Remark</span>
                <textarea
                  className="merchant-form-input merchant-form-textarea"
                  name="businessDescription"
                  value={profileForm.businessDescription}
                  onChange={handleProfileChange}
                />
              </label>
            </div>
          )}

          {formError ? (
            <div className="merchant-inline-status merchant-inline-status-error">{formError}</div>
          ) : null}

          <div className="merchant-modal-actions">
            <button
              className="merchant-button merchant-button-outline"
              type="button"
              onClick={closeModal}
            >
              Cancel
            </button>
            <button
              className="merchant-button merchant-button-primary"
              type="submit"
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </MerchantModal>
    </div>
  );
}
